import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography,
  TextField, IconButton, Chip, CircularProgress, Divider,
} from '@mui/material';
import { Close as CloseIcon, ImageNotSupported as NoImageIcon } from '@mui/icons-material';
import axios from 'axios';

// Paleta para diferenciar almacenes por color
const ALMACEN_COLORS = [
  '#1976d2', '#2e7d32', '#ef6c00', '#8e24aa',
  '#00838f', '#c62828', '#6d4c41', '#455a64',
  '#ad1457', '#0277bd',
];

// Color estable por nombre de almacén (no depende del orden/índice)
function colorForAlmacen(nombre) {
  let hash = 0;
  const str = nombre || '';
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return ALMACEN_COLORS[hash % ALMACEN_COLORS.length];
}

const STOCK_BAJO_UMBRAL = 2;

// "ALMACEN U-1A" -> "Almacen U-1A" (los tokens con dígitos/códigos se dejan tal cual)
function formatAlmacenNombre(nombre) {
  return (nombre || '')
    .split(' ')
    .map(w => (/\d/.test(w) ? w : w.charAt(0) + w.slice(1).toLowerCase()))
    .join(' ');
}

const ZOOM_SIZE_KEY = 'producto-zoom-size';

// Popup de imagen con zoom redimensionable, código, precio y stock por almacén (con color).
// Diseñado para poder capturar solo la franja superior (nombre + código + imagen) sin
// arrastrar precio/stock, que quedan separados abajo por un divisor.
export default function ProductoImagenZoomDialog({ open, onClose, src, nombre, codigo, precio, productoId }) {
  const [zoomSize, setZoomSize] = useState(() => {
    try {
      const saved = localStorage.getItem(ZOOM_SIZE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return { width: 500, height: 500 };
  });
  const [stock, setStock] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  useEffect(() => {
    if (!open || !productoId) { setStock([]); return; }
    setStockLoading(true);
    axios.get(`/api/productos/${productoId}/stock-almacenes`)
      .then(r => setStock(Array.isArray(r.data) ? r.data : []))
      .catch(() => setStock([]))
      .finally(() => setStockLoading(false));
  }, [open, productoId]);

  const updateZoomSize = (patch) => {
    setZoomSize(prev => {
      const next = { ...prev, ...patch };
      try { localStorage.setItem(ZOOM_SIZE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const startZoomResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const startW = zoomSize.width, startH = zoomSize.height;
    const maxW = 1400, maxH = 1400;

    const onMove = (ev) => {
      const w = Math.min(maxW, Math.max(250, Math.round(startW + (ev.clientX - startX))));
      const h = Math.min(maxH, Math.max(250, Math.round(startH + (ev.clientY - startY))));
      updateZoomSize({ width: w, height: h });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [zoomSize]); // eslint-disable-line

  const stockConSaldo = stock
    .filter(r => Number(r.stock) > 0)
    .sort((a, b) => Number(b.stock) - Number(a.stock));

  return (
    <Dialog open={open} onClose={onClose}
      PaperProps={{
        sx: {
          width: zoomSize.width, height: zoomSize.height,
          overflow: 'hidden', position: 'relative',
          minWidth: 250, minHeight: 250,
          maxWidth: '95vw', maxHeight: '95vh',
          display: 'flex', flexDirection: 'column',
        },
      }}
    >
      {/* Zona "segura" para captura: nombre + código, sin precio ni stock */}
      <DialogTitle sx={{ pb: 1, flexShrink: 0 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle1" noWrap>{nombre}</Typography>
            {codigo && (
              <Typography variant="caption" color="text.secondary">Cód: {codigo}</Typography>
            )}
          </Box>
          <TextField label="Ancho" type="number" size="small" value={zoomSize.width}
            onChange={e => updateZoomSize({ width: Math.min(1400, Math.max(250, Number(e.target.value) || 250)) })}
            sx={{ width: 90 }} inputProps={{ min: 250, max: 1400 }} />
          <TextField label="Alto" type="number" size="small" value={zoomSize.height}
            onChange={e => updateZoomSize({ height: Math.min(1400, Math.max(250, Number(e.target.value) || 250)) })}
            sx={{ width: 90 }} inputProps={{ min: 250, max: 1400 }} />
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', p: 1,
      }}>
        {src ? (
          <img src={src} alt={nombre}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" gap={1} color="text.disabled">
            <NoImageIcon sx={{ fontSize: 90, opacity: 0.4 }} />
            <Typography variant="body2" color="text.disabled">Sin imagen</Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      {/* Pie de página: precio y stock, fuera de la zona que normalmente se captura */}
      <Box sx={{ flexShrink: 0, backgroundColor: '#fafafa', px: 2, py: 1 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Precio: <b>{precio != null ? `S/ ${Number(precio).toFixed(2)}` : '-'}</b>
        </Typography>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
          <Typography variant="body2" component="span">Stock:</Typography>
          {stockLoading ? (
            <CircularProgress size={14} />
          ) : stockConSaldo.length ? (
            stockConSaldo.map((r) => {
              const qty = Number(r.stock);
              const bajo = qty <= STOCK_BAJO_UMBRAL;
              return (
                <Chip
                  key={r.almacen}
                  size="small"
                  label={
                    <Box display="flex" alignItems="center" gap={0.6}>
                      <span>{formatAlmacenNombre(r.almacen)}</span>
                      <Box component="span" sx={{
                        backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '8px',
                        px: 0.7, fontWeight: 800,
                      }}>
                        {qty.toFixed(0)}
                      </Box>
                    </Box>
                  }
                  sx={{
                    backgroundColor: colorForAlmacen(r.almacen),
                    color: '#fff', fontSize: '0.72rem', height: 24,
                    border: bajo ? '2px solid #ffca28' : 'none',
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              );
            })
          ) : (
            <Chip label="Sin stock" size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
          )}
        </Box>
      </Box>

      <Box
        onMouseDown={startZoomResize}
        sx={{
          position: 'absolute', right: 0, bottom: 0, width: 20, height: 20,
          cursor: 'nwse-resize', zIndex: 2,
          background: 'linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.35) 50%)',
        }}
      />
    </Dialog>
  );
}
